import { Geolocation } from '@capacitor/geolocation';
import { useJsApiLoader } from '@react-google-maps/api';
import { googleLibraries } from 'app/core/api/api-core/api-constants';
import { ProductDefect } from 'app/core/api/product-defect/api-product-defect.types';
import {
  useAssignmentOpenRepair,
  useInitiateChat,
  useSearchRepairers,
} from 'app/core/api/repairer/api-repairer';
import { RepairerSearchItem } from 'app/core/api/repairer/api-repairer.types';
import { appEnv } from 'app/core/environment';
import { getErrorMsg } from 'app/core/errors/error-utils';
import Button from 'app/shared/components/button/Button';
import { MapView } from 'app/shared/components/google-map/MapView';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import LoadingOverlay from 'app/shared/page-parts/LoadingOverlay';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import styledConsts from 'app/styles/styledConsts';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import {
  useComponentIsStillLoaded,
  useEffectOnce,
  useUrlParams,
} from 'app/tools/hooks/misc-hooks';
import React, { useEffect, useState } from 'react';
import { GeocodeCoords, GeocodeSearchResult } from 'react-geocode';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components/macro';
import GooglePlacesTextbox from './components/GooglePlacesTextbox';
import RepairerDetails from './components/RepairerDetails';

const CENTER_OF_LONDON = { lat: 51.507396, lng: -0.127673 };

const SearchContainer = styled.div`
  z-index: 2;
  width: 100%;
  position: absolute;
`;
const SearchHereBtn = styled(Button)`
  ${styledMixins.roundCorners}
  padding: 0 ${styledFuncs.pad()};
  width: 130px;
  left: calc(50% - 65px);
  background: white;
  color: ${styledConsts.cPrimary};
  min-height: 15px;
  font-size: ${styledFuncs.pxToRem(13)};
  font-weight: normal;
  &:hover {
    background: white;
  }
`;
export default function SearchRepairer(): JSX.Element {
  const { id: assignmentId } = useUrlParams(['id']);
  const history = useHistory();
  const { showSnack, showDialog } = useShowInfo();
  const stillLoaded = useComponentIsStillLoaded();

  const { isLoaded: isGoogleJSLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: appEnv.GOOGLE_API_KEY,
    libraries: googleLibraries,
  });

  const createdDefect = history.location.state as ProductDefect;

  const [currentLocation, setCurrentLocation] =
    useState<GeocodeCoords>(CENTER_OF_LONDON);
  const [draggedLocation, setDraggedLocation] =
    useState<GeocodeCoords>();
  const [selectedRepairer, setSelectedRepairer] =
    useState<RepairerSearchItem>();
  const [isPlacesResultShowing, setIsPlacesResultShowing] =
    useState<boolean>();
  const [alreadyLoaded, setAlreadyLoaded] = useState(false);
  const [repairers, setRepairers] = useState<RepairerSearchItem[]>();

  const { request$: loadRepairer$, sending: searching } =
    useSearchRepairers();
  const { request$: initiateChat } = useInitiateChat();

  const { resource: openRepairCaseId, loading: loadingOpenCase } =
    useAssignmentOpenRepair(+assignmentId);

  useEffect(() => {
    if (openRepairCaseId) {
      history.replace(`${SharedRoutePath.CHAT_LIST}/all`);
      history.push(`${SharedRoutePath.CHAT}/${openRepairCaseId}`);
    }
  }, [history, assignmentId, openRepairCaseId]);

  const startChat = (id: number) => {
    initiateChat({
      repairer: id,
      productDefect: createdDefect.id || 0,
      id,
    }).subscribe({
      next: chatItem => {
        history.push(
          `${SharedRoutePath.CHAT}/${chatItem?.id || ''}`,
          true
        );
      },
      error: (e: unknown) => {
        showSnack({
          message: getErrorMsg(e),
        });
      },
    });
  };

  const requestRepairer = (location: GeocodeCoords) => {
    loadRepairer$({
      assignedProductId: assignmentId,
      lat: location.lat,
      long: location.lng,
      maxDistance: 8, // distance in km
    }).subscribe(({ searchResults, fallback }) => {
      setRepairers(searchResults);
      setSelectedRepairer(
        searchResults.length ? searchResults[0] : undefined
      );
      setDraggedLocation(undefined);
      setIsPlacesResultShowing(false);
      setCurrentLocation(location);
      if (!alreadyLoaded) {
        setAlreadyLoaded(true);
      }
      if (!searchResults.length && fallback && alreadyLoaded) {
        showDialog({
          title: fallback.displayName,
          message: fallback.overview,
          actions: [
            {
              text: 'Chat',
              callback: () => {
                startChat(fallback.id);
              },
            },
            {
              text: 'Cancel',
            },
          ], // use default action button (close)
        });
      }
    });
  };

  useEffectOnce(() => {
    Geolocation.getCurrentPosition()
      .then(position => {
        if (!stillLoaded()) return;
        let targetLocation = currentLocation;

        if (position?.coords?.latitude) {
          targetLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCurrentLocation(targetLocation);
        } else {
          showSnack({
            message: 'Current location data missing',
          });
        }
        requestRepairer(targetLocation);
      })
      .catch((e: unknown) => {
        if (!stillLoaded()) return;
        showSnack({
          message: getErrorMsg(e),
        });
        requestRepairer(currentLocation);
      });
  });

  const onPlaceSelected = (res: GeocodeSearchResult) => {
    requestRepairer(res.geometry.location);
  };

  if (!createdDefect) {
    history.push(SharedRoutePath.HOME);
  }
  return (
    <>
      <SearchContainer>
        <LoadingOverlay
          loadingComplete={!searching && !loadingOpenCase}
          translucent
          color="primary"
        />
        {isGoogleJSLoaded && (
          <GooglePlacesTextbox
            onPlaceSelected={onPlaceSelected}
            setIsPlacesResultShowing={setIsPlacesResultShowing}
          />
        )}
        {draggedLocation && !isPlacesResultShowing && (
          <SearchHereBtn
            onClick={() => {
              requestRepairer({
                lat: draggedLocation.lat,
                lng: draggedLocation.lng,
              });
            }}
          >
            Search this area
          </SearchHereBtn>
        )}
      </SearchContainer>
      {isGoogleJSLoaded && (
        <MapView
          repairers={repairers as RepairerSearchItem[]}
          selectedRepairerId={selectedRepairer?.id || NaN}
          onRepairerSelected={(repairer: RepairerSearchItem) => {
            setSelectedRepairer(repairer);
          }}
          currentLocation={currentLocation}
          onMapDragged={(location: GeocodeCoords) => {
            setCurrentLocation({
              lat: location.lat,
              lng: location.lng,
            });
            setDraggedLocation(location);
          }}
        />
      )}
      {selectedRepairer && repairers && (
        <RepairerDetails
          repairers={repairers}
          selectedRepairer={selectedRepairer}
          setSelectedRepairer={setSelectedRepairer}
        />
      )}
    </>
  );
}
